import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
}

interface NavItem {
  label: string;
  to: string;
  match: (pathname: string) => boolean;
  icon: ReactNode;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      className="h-[18px] w-[18px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const navGroups: NavGroup[] = [
  {
    label: 'Discover',
    items: [
      {
        label: 'Overview',
        to: '/',
        match: (pathname) => pathname === '/',
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v4H4zM14 15h6v4h-6z"
            />
          </Icon>
        ),
      },
      {
        label: 'Models',
        to: '/models',
        match: (pathname) => pathname === '/models' || /^\/models\/(?!all$)/.test(pathname),
        icon: (
          <Icon>
            <path strokeLinecap="round" strokeLinejoin="round" d="m12 3.5 7 4v8l-7 4-7-4v-8l7-4Z" />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m12 11.5 7-4M12 11.5l-7-4m7 4v8"
            />
          </Icon>
        ),
      },
      {
        label: 'Deployments',
        to: '/models/all',
        match: (pathname) => pathname === '/models/all',
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 7.5 12 5l5 2.5-5 2.5-5-2.5ZM7 12l5 2.5 5-2.5M7 16.5l5 2.5 5-2.5"
            />
          </Icon>
        ),
      },
      {
        label: 'Providers',
        to: '/providers',
        match: (pathname) => pathname === '/providers' || pathname.startsWith('/providers/'),
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 7.5a4 4 0 0 1 8 0M5 14a4 4 0 0 1 6.5-3.1M12.5 10.9A4 4 0 0 1 19 14M7.5 17.5h9"
            />
          </Icon>
        ),
      },
    ],
  },
  {
    label: 'Decide',
    items: [
      {
        label: 'Compare',
        to: '/compare/models',
        match: (pathname) => pathname.startsWith('/compare/') && !pathname.includes('residency'),
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 5v14M17 5v14M4.5 8 7 5l2.5 3M14.5 16 17 19l2.5-3"
            />
          </Icon>
        ),
      },
      {
        label: 'Residency',
        to: '/compliance/residency',
        match: (pathname) => pathname.includes('residency'),
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"
            />
          </Icon>
        ),
      },
      {
        label: 'Compare deployments',
        to: '/compare',
        match: (pathname) => pathname === '/compare',
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 6.5h15M4.5 12h15M4.5 17.5h15M9.5 4v16"
            />
          </Icon>
        ),
      },
      {
        label: 'Governance',
        to: '/governance/aws',
        match: (pathname) => pathname.startsWith('/governance') || pathname === '/compliance',
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 3.5 18.5 6v5.3c0 4.1-2.7 7-6.5 8.7-3.8-1.7-6.5-4.6-6.5-8.7V6L12 3.5Zm-2.5 8.3 1.7 1.7 3.5-3.7"
            />
          </Icon>
        ),
      },
    ],
  },
  {
    label: 'Monitor',
    items: [
      {
        label: 'Changes',
        to: '/changes',
        // Model catalogue changes only; /changelog is ModelMap's own release notes.
        match: (pathname) => pathname === '/changes' || pathname.startsWith('/changes/'),
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6l4 2M20.5 12a8.5 8.5 0 1 1-2.3-5.8M20.5 4.5v5h-5"
            />
          </Icon>
        ),
      },
      {
        label: 'Data status',
        to: '/status',
        match: (pathname) => pathname === '/status',
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 19.5h15M7 16V9m5 7V5m5 11v-4"
            />
          </Icon>
        ),
      },
    ],
  },
  {
    label: 'Learn',
    items: [
      {
        label: 'Guides',
        to: '/guides',
        match: (pathname) => pathname.startsWith('/guides') || pathname.startsWith('/blog'),
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 5.5h10a2 2 0 0 1 2 2v12H7a2 2 0 0 1-2-2v-12M7 5.5v12a2 2 0 0 1-2 2M9 9h6M9 12h6M9 15h4"
            />
          </Icon>
        ),
      },
      {
        label: 'About ModelMap',
        to: '/about',
        match: (pathname) => pathname === '/about',
        icon: (
          <Icon>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 10v6M12 7h.01"
            />
          </Icon>
        ),
      },
      {
        label: 'Privacy',
        to: '/privacy',
        match: (pathname) => pathname === '/privacy',
        icon: (
          <Icon>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3.5 18.5 6v5.3c0 4.1-2.7 7-6.5 8.7-3.8-1.7-6.5-4.6-6.5-8.7V6L12 3.5Z" />
          </Icon>
        ),
      },
    ],
  },
];

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function SidebarContent({ pathname, onNavigate }: { pathname: string; onNavigate: () => void }) {
  return (
    <nav aria-label="Primary navigation" className="flex h-full flex-col bg-white">
      <div className="flex-1 space-y-5 overflow-y-auto px-3 py-5">
        {navGroups.map((group) => (
          <div key={group.label}>
            <div className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600">
              {group.label}
            </div>
            <div className="space-y-1">
              {group.items.map((item) => {
                const active = item.match(pathname);
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={`group flex min-h-11 items-center gap-3 rounded-xl px-3 text-[13px] font-semibold transition-colors ${
                      active
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition ${
                        active
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-slate-500 group-hover:text-slate-800'
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="m-3 hidden rounded-xl border border-slate-200 bg-slate-50 p-3 lg:block">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-800">
          <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
          Evidence-led catalog
        </div>
        <p className="mt-1.5 text-[11px] leading-4 text-slate-600">
          Provider claims are labelled separately from measured evidence.
        </p>
      </div>
    </nav>
  );
}

export function Sidebar({ open, onClose, triggerRef }: SidebarProps) {
  const location = useLocation();
  const mobileDialogRef = useRef<HTMLElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const backgroundElements = [
      document.getElementById('app-topbar'),
      document.getElementById('main-content'),
      document.getElementById('comparison-fab-layer'),
    ].filter((element): element is HTMLElement => Boolean(element));

    if (!open) {
      if (wasOpenRef.current) triggerRef.current?.focus();
      wasOpenRef.current = false;
      return;
    }

    wasOpenRef.current = true;
    backgroundElements.forEach((element) => {
      element.inert = true;
    });

    const frame = window.requestAnimationFrame(() => {
      const dialog = mobileDialogRef.current;
      const activeLink = dialog?.querySelector<HTMLElement>('[aria-current="page"]');
      const firstLink = dialog?.querySelector<HTMLElement>('a[href]');
      (activeLink ?? firstLink)?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const dialog = mobileDialogRef.current;
      const focusable = dialog
        ? Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
            (element) => !element.hasAttribute('disabled') && element.tabIndex !== -1
          )
        : [];

      if (focusable.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown);
      backgroundElements.forEach((element) => {
        element.inert = false;
      });
    };
  }, [onClose, open, triggerRef]);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/45 backdrop-blur-[2px] lg:hidden"
          onClick={onClose}
          data-testid="mobile-navigation-backdrop"
          aria-hidden="true"
        />
      )}

      {open && (
        <aside
          ref={mobileDialogRef}
          id="mobile-navigation"
          role="dialog"
          aria-modal="true"
          aria-label="Primary navigation"
          tabIndex={-1}
          className="fixed inset-y-0 left-0 z-50 w-[286px] border-r border-slate-200 bg-white shadow-2xl outline-none lg:hidden"
        >
          <div className="flex h-[68px] items-center justify-between border-b border-slate-200 px-5">
            <span className="text-sm font-bold text-slate-950">Explore ModelMap</span>
            <button
              type="button"
              onClick={onClose}
              data-testid="mobile-navigation-close"
              aria-label="Close navigation menu"
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="h-[calc(100%-68px)]">
            <SidebarContent pathname={location.pathname} onNavigate={onClose} />
          </div>
        </aside>
      )}

      <aside className="hidden w-[232px] flex-shrink-0 border-r border-slate-200/80 bg-white lg:flex lg:flex-col">
        <SidebarContent pathname={location.pathname} onNavigate={onClose} />
      </aside>
    </>
  );
}
